import axios from 'axios';
import { createSlice } from '@reduxjs/toolkit';
import url from '../../constants/config';
import { toasterOn } from '../../shared/store/actions';

const getAuthHeader = () => ({
  Authorization: `Bearer ${window.localStorage.getItem('authorization')}`,
});

export const conversationsSlice = createSlice({
  name: 'conversations',
  initialState: {
    list: [],
    activeThread: null,
    messages: [],
    nextCursor: null,
    unreadCount: 0,
    loading: false,
  },
  reducers: {
    setConversations: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.list = action.payload;
      // eslint-disable-next-line no-param-reassign
      state.unreadCount = action.payload.reduce((sum, c) => {
        const msgUnread = c.unreadCount || 0;
        const markedUnread = (c.markedUnreadByCoach || c.markedUnreadByPlayer) && msgUnread === 0 ? 1 : 0;
        return sum + msgUnread + markedUnread;
      }, 0);
    },
    setActiveThread: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.activeThread = action.payload.conversation;
      // eslint-disable-next-line no-param-reassign
      state.messages = action.payload.messages;
      // eslint-disable-next-line no-param-reassign
      state.nextCursor = action.payload.nextCursor;
    },
    prependMessages: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.messages = [...action.payload.messages, ...state.messages];
      // eslint-disable-next-line no-param-reassign
      state.nextCursor = action.payload.nextCursor;
    },
    appendMessage: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.messages = [...state.messages, action.payload];
    },
    setLoading: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.loading = action.payload;
    },
    clearThread: state => {
      // eslint-disable-next-line no-param-reassign
      state.activeThread = null;
      // eslint-disable-next-line no-param-reassign
      state.messages = [];
      // eslint-disable-next-line no-param-reassign
      state.nextCursor = null;
    },
    setUnreadCount: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.unreadCount = action.payload;
    },
  },
});

export const {
  setConversations,
  setActiveThread,
  prependMessages,
  appendMessage,
  setLoading,
  clearThread,
  setUnreadCount,
} = conversationsSlice.actions;

export const fetchConversations = () => async dispatch => {
  try {
    dispatch(setLoading(true));
    const res = await axios.get(`${url}/conversations`, { headers: getAuthHeader() });
    dispatch(setConversations(res.data));
  } catch (err) {
    dispatch(toasterOn({ message: 'Failed to load conversations', type: 'failed' }));
  } finally {
    dispatch(setLoading(false));
  }
};

export const fetchThread = id => async dispatch => {
  try {
    dispatch(setLoading(true));
    const res = await axios.get(`${url}/conversations/${id}`, { headers: getAuthHeader() });
    dispatch(setActiveThread(res.data));
  } catch (err) {
    dispatch(toasterOn({ message: 'Failed to load conversation', type: 'failed' }));
  } finally {
    dispatch(setLoading(false));
  }
};

export const fetchMoreMessages = (id, cursor) => async dispatch => {
  try {
    const res = await axios.get(`${url}/conversations/${id}/messages?before=${cursor}`, {
      headers: getAuthHeader(),
    });
    dispatch(prependMessages(res.data));
  } catch (err) {
    dispatch(toasterOn({ message: 'Failed to load older messages', type: 'failed' }));
  }
};

export const openOrSendMessage = ({ playerId, text, file }) => async dispatch => {
  try {
    dispatch(setLoading(true));
    const headers = getAuthHeader();
    let res;
    if (file) {
      const formData = new FormData();
      formData.append('playerId', playerId);
      if (text) formData.append('text', text);
      formData.append('attachment', file);
      res = await axios.post(`${url}/conversations`, formData, { headers });
    } else {
      res = await axios.post(
        `${url}/conversations`,
        { playerId, text },
        { headers }
      );
    }
    dispatch(toasterOn({ message: 'Message sent!', type: 'success' }));
    return res.data;
  } catch (err) {
    dispatch(toasterOn({ message: 'Failed to send message', type: 'failed' }));
    return null;
  } finally {
    dispatch(setLoading(false));
  }
};

export const sendReply = ({ conversationId, text, file, draftRevision }) => async dispatch => {
  try {
    let response;
    const headers = getAuthHeader();
    if (file) {
      const formData = new FormData();
      if (draftRevision !== undefined) formData.append('draftRevision', String(draftRevision));
      if (text) formData.append('text', text);
      formData.append('attachment', file);
      response = await axios.post(
        `${url}/conversations/${conversationId}/messages`,
        formData,
        { headers }
      );
    } else {
      response = await axios.post(
        `${url}/conversations/${conversationId}/messages`,
        { text, draftRevision },
        { headers }
      );
    }
    dispatch(appendMessage(response.data.message));
    return response.data.message;
  } catch (err) {
    dispatch(toasterOn({ message: 'Failed to send reply', type: 'failed' }));
    return null;
  }
};

export const fetchUnreadCount = () => async dispatch => {
  try {
    const res = await axios.get(`${url}/conversations/unread-count`, { headers: getAuthHeader() });
    dispatch(setUnreadCount(res.data.count));
  } catch (err) {
    // silently fail — non-critical background poll
  }
};

export const markThreadRead = conversationId => async dispatch => {
  try {
    await axios.patch(`${url}/conversations/${conversationId}/read`, {}, { headers: getAuthHeader() });
  } catch (err) {
    // silently fail — read receipts are non-critical
  }
};

export const markConversationUnread = conversationId => async dispatch => {
  try {
    await axios.patch(`${url}/conversations/${conversationId}/mark-unread`, {}, { headers: getAuthHeader() });
  } catch (err) {
    // silently fail
  }
};

export default conversationsSlice.reducer;
